import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Box,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AiFillFileExcel } from "react-icons/ai";

import LoadingButton from "components/@extended/LoadingButton";
import api from "utils/api";

import {
  GetMaterialTypeRanking,
  GetMemberTypeRanking,
  GetMostActiveMembers,
  GetTopFrequentlyUsedBooks,
} from "../api/hooks";
import useTopBooksPerformanceStore from "../store/TopBooksPerformanceStore";
import { resolveDateRange } from "../store/dateRangeHelper";
import TopBooksChart from "./TopBooksChart";
import VisualTabularCard from "./VisualTabularCard";
import { ExportButton } from "components/ExportButton";

const CHART_COLOR = "#5B8FF9";

function useResolvedDateRange() {
  const { dateRangePreset, customFromDate, customToDate } =
    useTopBooksPerformanceStore();
  return useMemo(
    () => resolveDateRange(dateRangePreset, customFromDate, customToDate),
    [dateRangePreset, customFromDate, customToDate],
  );
}

const CardLoader = () => (
  <Box
    sx={{
      height: 260,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress />
  </Box>
);

//#region Generic ranking bar chart (Members / Member Types / Material Types)
interface RankingBarChartProps {
  data: any[] | undefined;
  nameKey: string;
  height?: number;
}

const RankingBarChart = ({
  data,
  nameKey,
  height = 240,
}: RankingBarChartProps) => {
  const chartData = useMemo(
    () =>
      [...(data ?? [])]
        .map((item) => ({
          name: String(item?.[nameKey] ?? ""),
          totalIssueCount: item?.totalIssueCount ?? 0,
        }))
        .reverse(),
    [data, nameKey],
  );

  if (!chartData.length) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={110}
            tick={{ fontSize: 12 }}
          />
          <Tooltip />
          <Bar dataKey="totalIssueCount" fill={CHART_COLOR} barSize={16}>
            <LabelList dataKey="totalIssueCount" position="right" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
//#endregion

//#region Card 1: Top 10 Most Issued Books
const TopIssuedBooksCard = () => {
  const { fromDate, toDate } = useResolvedDateRange();

  const { data: response, isFetching } = GetTopFrequentlyUsedBooks({
    pagingModel: { page: 0, pageSize: 10 },
    filterModel: {
      memberTypeID: null,
      materialTypeID: null,
      fromDate,
      toDate,
    },
  });

  const rows = response?.apiData?.data ?? [];

  if (isFetching) {
    return <CardLoader />;
  }

  return (
    <VisualTabularCard
      title="Top 10 Most Issued Books"
      chart={<TopBooksChart data={rows} height={280} />}
      table={
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Title</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uniqueID} hover>
                <TableCell>{row.rank}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell align="right">{row.totalIssueCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    />
  );
};
//#endregion

//#region Card 2 & 3: Most Active Members (Student / Staff)
interface MostActiveMembersCardProps {
  title: string;
  memberTypeCode: string;
}

const MostActiveMembersCard = ({
  title,
  memberTypeCode,
}: MostActiveMembersCardProps) => {
  const { fromDate, toDate } = useResolvedDateRange();
  const [exportLoading, setExportLoading] = useState(false);

  const { data: response, isFetching } = GetMostActiveMembers({
    pagingModel: { page: 0, pageSize: 5 },
    filterModel: {
      memberTypeCode,
      materialTypeID: null,
      fromDate,
      toDate,
    },
  });

  const rows = response?.apiData?.data ?? [];

  const handleExport = async () => {
    setExportLoading(true);
    await api.downloadExcel(
      "api/Dashboard/exportMostActiveMembers",
      memberTypeCode === "Staff" ? "MostActiveStaffMembers_" : "MostActiveStudents_",
      { memberTypeCode, materialTypeID: null, fromDate, toDate },
      setExportLoading,
    );
  };

  if (isFetching) {
    return <CardLoader />;
  }

  return (
    <VisualTabularCard
      title={title}
      headerAction={
        <ExportButton
          groupName={
            memberTypeCode === "Staff"
              ? "MostActiveStaffMembers"
              : "MostActiveStudents"
          }
          fileName={
            memberTypeCode === "Staff"
              ? "MostActiveStaffMembers"
              : "MostActiveStudents"
          }
          reportTitle={title}
          params={() => ({
            memberTypeCode,
            materialTypeID: null,
            fromDate,
            toDate,
          })}
          disabled={rows.length === 0}
          size="small"
          buttonText="Export"
        />
      }
      chart={<RankingBarChart data={rows} nameKey="memberName" />}
      table={
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Member Name</TableCell>
              <TableCell>Member Code</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uniqueID} hover>
                <TableCell>{row.rank}</TableCell>
                <TableCell>{row.memberName}</TableCell>
                <TableCell>{row.memberCode}</TableCell>
                <TableCell align="right">{row.totalIssueCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    />
  );
};
//#endregion

//#region Card 4: Most Active Member Types
const MemberTypeRankingCard = () => {
  const { fromDate, toDate } = useResolvedDateRange();

  const { data: response, isFetching } = GetMemberTypeRanking({
    materialTypeID: null,
    fromDate,
    toDate,
  });

  const rows = response?.apiData ?? [];

  if (isFetching) {
    return <CardLoader />;
  }

  return (
    <VisualTabularCard
      title="Most Active Member Types"
      chart={<RankingBarChart data={rows} nameKey="memberTypeName" />}
      table={
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Member Type</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uniqueID} hover>
                <TableCell>{row.rank}</TableCell>
                <TableCell>{row.memberTypeName}</TableCell>
                <TableCell align="right">{row.totalIssueCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    />
  );
};
//#endregion

//#region Card 5: Most Used Material Types
const MaterialTypeRankingCard = () => {
  const { fromDate, toDate } = useResolvedDateRange();

  const { data: response, isFetching } = GetMaterialTypeRanking({
    memberTypeID: null,
    fromDate,
    toDate,
  });

  const rows = response?.apiData ?? [];

  if (isFetching) {
    return <CardLoader />;
  }

  return (
    <VisualTabularCard
      title="Most Used Material Types"
      chart={<RankingBarChart data={rows} nameKey="materialTypeName" />}
      table={
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Material Type</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uniqueID} hover>
                <TableCell>{row.rank}</TableCell>
                <TableCell>{row.materialTypeName}</TableCell>
                <TableCell align="right">{row.totalIssueCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    />
  );
};
//#endregion

const PerformanceReportSection = () => {
  return (
    <Grid container spacing={2.5}>
      {/* <Grid item xs={12} md={6} lg={4}>
        <TopIssuedBooksCard />
      </Grid> */}
      <Grid item xs={12} md={6} lg={6}>
        <MostActiveMembersCard
          title="Most Active Students"
          memberTypeCode="Student"
        />
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <MostActiveMembersCard
          title="Most Active Staff Members"
          memberTypeCode="Staff"
        />
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <MemberTypeRankingCard />
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <MaterialTypeRankingCard />
      </Grid>
    </Grid>
  );
};

export default PerformanceReportSection;
